import { computed } from "vue";
import { storeToRefs } from "pinia";
import type { AdminPermissionValue } from "~~/schemas/admin-role";

type Perm = AdminPermissionValue;

/**
 * Read-only access checker for the current admin user.
 *
 * - `permissions`: flattened Set from user.role.permissions
 * - `can(perm)`: true if user has the permission (OR logic if array)
 * - `role`: shortcut to current AdminRole
 *
 * Mirrors artech-client/app/composables/use-access.ts but uses single-role
 * AdminUser instead of multi-role WorkspaceUser.
 */
export function useAccess() {
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  const role = computed(() => user.value?.role ?? null);

  const permissions = computed<Set<string>>(() => {
    const list = user.value?.role?.permissions ?? [];
    return new Set(list);
  });

  function can(perm: Perm | Perm[]): boolean {
    if (!user.value) return false;
    const list = Array.isArray(perm) ? perm : [perm];
    if (list.length === 0) return true;
    return list.some((p) => permissions.value.has(p));
  }

  function canAll(perm: Perm[]): boolean {
    if (!user.value) return false;
    return perm.every((p) => permissions.value.has(p));
  }

  return { role, permissions, can, canAll };
}
